import { motion } from "framer-motion";

const CHAIN_IMG =
  "https://static.prod-images.emergentagent.com/jobs/174e7555-4c1b-4694-85af-3ff24499f4d9/images/d3e007ef997a79a3c60b691758867812c4cc6f7a5f0dc05fd63c4f572535063a.png";

const solvency = [
  { name: "DETERMINATE", desc: "One answer is warranted; model must find it." },
  { name: "UNDERDETERMINED", desc: "Evidence insufficient; correct move is to abstain." },
  { name: "INSUFFICIENT", desc: "Premises leave the claim unsupported but not false." },
  { name: "CONTRADICTORY", desc: "Premises refute each other; recognition itself is the signal." },
];

const eciParts = [
  { k: "SDA", v: "Solvency-class Discrimination Accuracy" },
  { k: "CA", v: "Calibrated Abstention" },
  { k: "RP", v: "Revision Precision" },
  { k: "1−ECE", v: "Expected-Calibration-Error complement" },
  { k: "1−HGI", v: "Hallucinated-Grounding Index complement" },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-32 px-6 lg:px-10 border-t border-white/5"
    >
      <div className="absolute inset-0 tech-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <div className="chapter-label mb-6">01 · The Thesis</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-neutral-100 max-w-4xl">
            Prometheus stole fire knowing an eternal punishment
            <span className="text-[#FF4D00] italic"> would follow</span>.
          </h2>
          <p className="mt-6 font-serif-display italic text-xl text-neutral-400 max-w-3xl">
            This benchmark was built the same way — to bring honest epistemic
            measurement to a field built on leaderboard flattery. The grind and
            the isolation are the price.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left column — text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 space-y-8"
          >
            <div className="border border-white/10 p-8 bg-[#111]/50">
              <div className="font-mono text-[11px] tracking-[0.35em] text-[#FF4D00] mb-4">
                LIVING · ANTI-CONTAMINATION · DATASET
              </div>
              <p className="text-neutral-300 leading-relaxed">
                PROMETHEUS-EBM ships with ~1,459 items generated by
                <span className="text-[#FF4D00]"> EpochGenerator</span> — a
                parameterized template engine with a{" "}
                <span className="text-neutral-100">3-tier anti-contamination architecture</span>.
                Every epoch mutates lexical surface, structural scaffolding, and
                ground-truth distractors, so the benchmark stays uncontaminated
                even after public release.
              </p>
            </div>

            <div className="border border-white/10 p-8 bg-[#111]/50">
              <div className="font-mono text-[11px] tracking-[0.35em] text-[#00F0FF] mb-5">
                FOUR · SOLVENCY · CLASSES
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {solvency.map((s) => (
                  <div
                    key={s.name}
                    data-testid={`solvency-${s.name.toLowerCase()}`}
                    className="border border-white/10 p-4 hover:border-[#FF4D00]/50 transition-colors"
                  >
                    <div className="font-mono text-xs text-[#FF4D00] tracking-widest">
                      {s.name}
                    </div>
                    <div className="mt-2 text-sm text-neutral-400 leading-snug">
                      {s.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-white/10 p-8 bg-[#111]/50">
              <div className="font-mono text-[11px] tracking-[0.35em] text-[#FF4D00] mb-4">
                ECI · EPISTEMIC · CALIBRATION · INDEX
              </div>
              <p className="text-neutral-300 leading-relaxed mb-6">
                ECI is a composite score — not a shortcut. Five orthogonal
                measurements, equal weight, with bootstrap confidence intervals,
                permutation tests, and d-prime / bias analyses behind every
                number.
              </p>
              <div className="flex flex-wrap gap-2">
                {eciParts.map((p) => (
                  <div
                    key={p.k}
                    className="border border-white/10 px-3 py-2 font-mono text-xs text-neutral-300 hover:border-[#00F0FF]/60 transition-colors"
                    title={p.v}
                    data-testid={`eci-part-${p.k}`}
                  >
                    <span className="text-[#00F0FF]">{p.k}</span>
                    <span className="text-neutral-600 mx-2">·</span>
                    <span className="text-neutral-500">{p.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right column — chains image + modes */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 space-y-6"
          >
            <div className="relative border border-white/10 aspect-[4/5] overflow-hidden bg-[#0a0a0a]">
              <img
                src={CHAIN_IMG}
                alt="breaking chains"
                className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-mono text-[11px] text-[#FF4D00] tracking-[0.3em] mb-2">
                  V5 · 4-STAGE · PROTOCOL
                </div>
                <div className="font-serif-display text-2xl text-neutral-100 leading-tight italic">
                  Solve → Self-Eval →<br />
                  Adversarial Probe → Revision
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="border border-[#00F0FF]/40 p-5 bg-[#00F0FF]/5">
                <div className="font-mono text-[11px] text-[#00F0FF] tracking-widest">
                  STANDARD
                </div>
                <div className="mt-2 font-serif-display text-3xl text-neutral-100">
                  200
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  fast run · multi-model
                </div>
              </div>
              <div className="border border-[#00F0FF]/40 p-5 bg-[#00F0FF]/5">
                <div className="font-mono text-[11px] text-[#00F0FF] tracking-widest">
                  EXTENDED
                </div>
                <div className="mt-2 font-serif-display text-3xl text-neutral-100">
                  350
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  curated · multi-model
                </div>
              </div>
              <div className="col-span-2 flex justify-center">
                <div className="w-[70%] border border-[#FF4D00]/40 p-5 bg-[#FF4D00]/5 flex flex-col items-center justify-center text-center">
                  <div className="font-mono text-[11px] text-[#FF4D00] tracking-widest">
                    DEEP_PROBE
                  </div>
                  <div className="mt-2 font-serif-display text-3xl text-neutral-100">
                    1,459
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    full-scale items · headline
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
